/** Exit-comparison report data source (P50).
 *
 * Calls `GET /api/reports/exit-comparison?from&to[&strategy_id]` — every
 * real trade in the range with what it actually did, plus the day's VIX
 * and totals. The Reports page builds its monthly summary from this.
 *
 * ⚠️ Carried four SIMULATED exit styles beside the real one until
 * 2026-08-16 (FIXED_TARGET / PIVOT_TRAIL / SWING_TARGET). The shadow
 * machinery was removed; the route name is unchanged because the page,
 * the drill-down and the EOD snapshot all read it for the trade list.
 */

import { getJson } from "@/lib/http";

export interface ExitOutcome {
  exit_price: number | null;
  exit_reason: string | null;
  exit_at: string | null;
  /** GROSS of charges — what the premium move was worth. */
  pnl: number | null;
  /** Brokerage, STT and the rest. LIVE only; null where unknown, which is
   * not the same as zero — a trade whose contract note has not been entered
   * yet was not free. */
  charges?: number | null;
}

export type ReportExecutionMode = "LIVE" | "PAPER";

export interface TradeComparison {
  trade_id: string;
  strategy_name: string;
  entry_at: string;
  /** The day this row's P&L belongs to: entry for an intraday trade, CLOSE
   * for a held positional basket. Group and filter days by THIS — a spread
   * opened 30 Jul and closed 4 Aug earned its money in August. */
  report_at: string;
  /** LIVE or PAPER — badged per row so real vs paper is never ambiguous. */
  execution_mode: ReportExecutionMode;
  contract_symbol: string | null;
  quantity: number | null;
  entry_fill_price: number | null;
  live: ExitOutcome;
  /**
   * Multi-leg basket fields. A basket has no single symbol, quantity or fill
   * price — its fills are per-leg — so those three are null on a POSITIONAL
   * row and the money lives here instead. Optional so an older cached payload
   * still parses as a plain intraday row.
   */
  horizon?: "INTRADAY" | "POSITIONAL";
  net_entry_cost?: number | null;
  net_exit_value?: number | null;
}

export interface ExitTypeTotals {
  closed: number;
  wins: number;
  pnl: number;
  charges?: number;
}

export interface VixDay {
  trade_date: string; // YYYY-MM-DD
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface ExitComparison {
  trades: TradeComparison[];
  live: ExitTypeTotals;
  /** Daily India VIX rows covering the range (P53). */
  vix: VixDay[];
}

export const reportsApi = {
  exitComparison(
    from: string,
    to: string,
    strategyId?: string,
    executionMode?: ReportExecutionMode,
  ): Promise<ExitComparison> {
    const params = new URLSearchParams({ from, to });
    if (strategyId) params.set("strategy_id", strategyId);
    if (executionMode) params.set("execution_mode", executionMode);
    return getJson<ExitComparison>(`/api/reports/exit-comparison?${params.toString()}`);
  },
  /** Months (YYYY-MM) that actually have trades — the only month tabs shown. */
  months(strategyId?: string, executionMode?: ReportExecutionMode): Promise<{ months: string[] }> {
    const params = new URLSearchParams();
    if (strategyId) params.set("strategy_id", strategyId);
    if (executionMode) params.set("execution_mode", executionMode);
    const qs = params.toString();
    const suffix = qs ? `?${qs}` : "";
    return getJson<{ months: string[] }>(`/api/reports/months${suffix}`);
  },
  /** Closed-trade P&L bucketed by day/month/year for the analytics view. */
  timeseries(
    granularity: Granularity,
    strategyId?: string,
    executionMode?: ReportExecutionMode,
  ): Promise<TimeseriesResponse> {
    const params = new URLSearchParams({ granularity });
    if (strategyId) params.set("strategy_id", strategyId);
    if (executionMode) params.set("execution_mode", executionMode);
    return getJson<TimeseriesResponse>(`/api/reports/timeseries?${params.toString()}`);
  },
};

export type Granularity = "day" | "month" | "year";

export interface TimeseriesBucket {
  period: string; // YYYY-MM-DD | YYYY-MM | YYYY (IST)
  pnl: number;
  trades: number;
  wins: number;
}

export interface TimeseriesResponse {
  granularity: string;
  buckets: TimeseriesBucket[];
}
